/**
 * Returns true if the value appears to contain HTML tags.
 */
export function isProbablyHtml(value = '') {
  return /<\/?[a-z][\s\S]*>/i.test(String(value));
}

/**
 * Cleans plain text while preserving normal paragraph spacing.
 * - normalizes line endings
 * - converts nbsp to regular spaces
 * - trims trailing spaces on lines
 * - collapses 3+ blank lines to 2
 */
export function cleanPlainTextDetail(text = '') {
  return String(text)
    .replace(/\r\n/g, '\n')
    .replace(/\u00A0/g, ' ')
    .replace(/[ \t]+\n/g, '\n')
    .replace(/\n[ \t]*\n[ \t]*\n+/g, '\n\n')
    .trim();
}

/**
 * Converts HTML to plain text.
 * If a platform-specific implementation is supplied, use that.
 * Otherwise fall back to a simple string-based conversion.
 */
export function htmlToPlainText(html = '', options = {}) {
  const { htmlToPlainTextImpl } = options;

  if (typeof htmlToPlainTextImpl === 'function') {
    return cleanPlainTextDetail(htmlToPlainTextImpl(html));
  }

  const prepped = String(html)
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>\s*/gi, '\n\n')
    .replace(/<\/div>\s*/gi, '\n')
    .replace(/<\/li>\s*/gi, '\n')
    .replace(/<\/h[1-6]>\s*/gi, '\n\n')
    .replace(/<\/pre>\s*/gi, '\n\n')
    .replace(/<li\b[^>]*>/gi, '• ');

  const plain = prepped.replace(/<[^>]+>/g, '');

  return cleanPlainTextDetail(
    plain
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/&#39;/gi, "'")
      .replace(/&quot;/gi, '"')
      .replace(/\r\n/g, '\n')
  );
}

/**
 * Shared entry point.
 * Web can inject a DOMParser/DOMPurify-based HTML normalizer.
 * Mobile can inject a mobile-safe implementation later.
 */
export function normalizeDetailContent(value = '', options = {}) {
  const { normalizeHtmlImpl, htmlToPlainTextImpl } = options;

  if (!value) {
    return {
      detailHtml: '',
      detail: '',
    };
  }

  if (!isProbablyHtml(value)) {
    const cleanedText = cleanPlainTextDetail(value);
    return {
      detailHtml: '',
      detail: cleanedText,
    };
  }

  const cleanedHtml =
    typeof normalizeHtmlImpl === 'function'
      ? normalizeHtmlImpl(value)
      : String(value).trim();

  return {
    detailHtml: cleanedHtml,
    detail: htmlToPlainText(cleanedHtml, { htmlToPlainTextImpl }),
  };
}